import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook that handles click outside of the referenced element
 * @param {Function} callback - Function to call when clicking outside
 * @param {boolean} isActive - Whether the hook is active (default: true)
 * @returns {React.RefObject} - Ref to attach to the element
 */
export const useClickOutside = (callback, isActive = true) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [callback, isActive]);

  return ref;
};


/**
 * Hook for dropdowns that need to detect clicks outside multiple refs
 * (e.g., trigger button and menu are separate elements)
 * @param {React.RefObject[]} refs - Array of refs to check
 * @param {Function} callback - Function to call when clicking outside all refs
 * @param {boolean} enabled - Whether the hook is active (default: true)
 */
export function useClickOutsideMultiple(refs, callback, enabled = true) {
  const handleClick = useCallback(
    (event) => {
      const isOutside = refs.every(
        (ref) => ref.current && !ref.current.contains(event.target)
      );
      if (isOutside) {
        callback();
      }
    },
    [refs, callback]
  );

  useEffect(() => {
    if (!enabled) return undefined;

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [enabled, handleClick]);
}